/**
 * V3IndieChatPanel — Panel / Drawer / Flyout chat surface
 *
 * Drop-in replacement for the MUI-based IndieChatPanel.js.
 * Supports the same modes (flyout | docked | page) and prop API.
 * Visual design is pixel-faithful to the Figma export.
 *
 * Usage:
 *   <V3IndieChatPanel
 *     mode="flyout"
 *     client={{ Name: 'Acme Boutique', ClientCode: '1234' }}
 *     onClose={handleClose}
 *     onToggleDock={handleDock}
 *   />
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Archive,
  FileText,
  History,
  MessageCircle,
  Minimize2,
  Maximize2,
  Paperclip,
  Plus,
  Sparkles,
  X,
  ChevronDown,
  Lock,
  Users,
} from 'lucide-react';
import '../orbit-chat.css';
import { SparkleIcon, HistoryLockIcon, HistorySharedIcon } from '../indie-chat.types';
import IndieChatPanel from '../IndieChatPanel';
import IndieChatBody from '../IndieChatBody';
import { IndieGeneratedFilesTab, SaveArtifactOverrides } from '../IndieGeneratedFiles';
import IndieChatInput from '../IndieChatInput';
import GlobalTip from '../../../providers/GlobalTip';
import { useOrbitChat, type OrbitChatViewHandoff } from '../../../hooks/useOrbitChat';
import { useOrbitMaintenanceMessage } from '../../../hooks/useOrbitMaintenanceMessage';
import { useOrbitMetadata } from '../../../hooks/useOrbitMetadata';
import { useOrbitFiles } from '../../../hooks/useOrbitFiles';
import { useOrbitArtifacts } from '../../../hooks/useOrbitArtifacts';
import { useUserPrompts } from '../../../hooks/useUserPrompts';
import { fetchOrbitConversations, type OrbitClient, type OrbitConversation } from '../../../services/OrbitChatService';
import { trackEvent } from '../../../services/AmplitudeService';
import type { ChatMessage } from '../indie-chat.types';
import { pickOrbitContextFiles, resolveOrbitContextUploadsFromFiles } from '../../../services/OrbitChatUploadUtils';
import OrbitMaintenanceBanner from '../OrbitMaintenanceBanner';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ChatMode = 'flyout' | 'docked' | 'page';

interface Client extends OrbitClient {
  /** Legacy alias — maps to ClientCode */
  ClientCode?: string | null;
  Name?: string | null;
}

interface Props {
  mode?: ChatMode;
  /** Laravel client object — provides ClientCode + Name */
  client?: Client;
  /** Laravel client int ID — required for live API calls */
  clientId?: number;
  onClose?: () => void;
  onToggleDock?: () => void;
  onOpenFullView?: (handoff?: OrbitChatViewHandoff) => void;
  pageAssistant?: any;
  initialPrompt?: string;
  promptRequestId?: number;
  autoSubmitInitialPrompt?: boolean;
  onInitialPromptSubmitted?: () => void;
  userName?: string;
}

type AssistantTab = 'ask-indie' | 'indie-insight';
type DrawerTab = 'chat' | 'history' | 'prompts' | 'files' | 'artifacts';

// ─── Component ───────────────────────────────────────────────────────────────

const V3IndieChatPanel: React.FC<Props> = ({
  mode = 'flyout',
  client,
  clientId = 0,
  onClose,
  onToggleDock,
  onOpenFullView,
  pageAssistant,
  initialPrompt,
  promptRequestId,
  autoSubmitInitialPrompt = false,
  onInitialPromptSubmitted,
  userName,
}) => {
  const tipContext = GlobalTip.useMessage();
  const isStandalonePage = mode === 'page';
  const isInlineMode = mode === 'docked' || mode === 'page';
  const isDrawerStyledMode = mode === 'flyout' || mode === 'docked';

  const clientLabel = client?.Name
    ? client.ClientCode
      ? `${client.Name} (${client.ClientCode})`
      : client.Name
    : 'No client selected';

  const {
    messages,
    isStreaming,
    streamingMessageId,
    sendMessage,
    resetConversation,
    conversationId,
    quotaExceeded,
    submitFeedback,
    isLoadingHistory,
    contextChips,
    contextUploads,
    addContextUploads,
    removeContextUpload,
  } = useOrbitChat({
    clientId: clientId ?? 0,
    client: client ?? {},
  });
  const { m1Prompts, clientPrompts } = useOrbitMetadata(clientId ?? 0);
  const {
    files,
    loading: filesLoading,
    uploading: filesUploading,
    uploadPolicy,
    uploadFile,
  } = useOrbitFiles(clientId ?? 0, conversationId);
  const { addPrompt } = useUserPrompts(clientId ?? 0);
  const { artifacts, loading: artifactsLoading, saveArtifact } = useOrbitArtifacts(clientId ?? 0);
  const { banner: maintenanceBanner } = useOrbitMaintenanceMessage(clientId ?? 0);
  const maintenanceModeActive = Boolean(
    maintenanceBanner.is_enabled && String(maintenanceBanner.message || '').trim(),
  );
  const hasPageAssistant = Boolean(pageAssistant?.enabled);
  const [activeAssistantTab, setActiveAssistantTab] = useState<AssistantTab>('ask-indie');
  const [activeDrawerTab, setActiveDrawerTab] = useState<DrawerTab>('chat');
  const [conversations, setConversations] = useState<OrbitConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const submittedPromptRequestId = useRef<number | undefined>(undefined);
  const drawerFileInputRef = useRef<HTMLInputElement | null>(null);
  const resolvedClientCode = client?.ClientCode || 'none';

  useEffect(() => {
    if (
      maintenanceModeActive
      || !autoSubmitInitialPrompt
      || !initialPrompt
      || !promptRequestId
      || submittedPromptRequestId.current === promptRequestId
    ) {
      return;
    }

    submittedPromptRequestId.current = promptRequestId;
    setActiveDrawerTab('chat');
    sendMessage(initialPrompt);
    onInitialPromptSubmitted?.();
  }, [autoSubmitInitialPrompt, initialPrompt, maintenanceModeActive, onInitialPromptSubmitted, promptRequestId, sendMessage]);

  useEffect(() => {
    if (!hasPageAssistant) {
      setActiveAssistantTab('ask-indie');
    }
  }, [hasPageAssistant]);

  useEffect(() => {
    if (hasPageAssistant && pageAssistant?.focusRequestToken) {
      setActiveAssistantTab('indie-insight');
    }
  }, [hasPageAssistant, pageAssistant?.focusRequestToken]);

  useEffect(() => {
    if (!isDrawerStyledMode || activeDrawerTab !== 'history' || !clientId) return undefined;
    let cancelled = false;
    setConversationsLoading(true);
    fetchOrbitConversations(clientId, 20)
      .then((result) => { if (!cancelled) setConversations(result); })
      // a failed load must not leave the previous list on screen (SPS-5749)
      .catch(() => { if (!cancelled) setConversations([]); })
      .finally(() => { if (!cancelled) setConversationsLoading(false); });
    return () => { cancelled = true; };
  }, [activeDrawerTab, clientId, isDrawerStyledMode]);

  const reportAssistantClientId = useMemo(() => (
    pageAssistant?.clientId ?? clientId ?? 0
  ), [clientId, pageAssistant?.clientId]);

  const handleAttachFile = async () => {
    const selectedFiles = await pickOrbitContextFiles();
    if (selectedFiles.length === 0) {
      return;
    }

    const { uploads, errors } = await resolveOrbitContextUploadsFromFiles(selectedFiles, {
      clientId: clientId && clientId > 0 ? clientId : undefined,
      conversationId,
    });

    if (uploads.length > 0) {
      addContextUploads(uploads);
      tipContext.showSuccess(
        uploads.length === 1
          ? 'File added to the current chat context'
          : `${uploads.length} files added to the current chat context`,
      );
    }

    if (errors.length > 0) {
      tipContext.show(errors[0]);
    }
  };

  const handleOpenStoredFilePicker = useCallback(() => {
    if (uploadPolicy?.files_remaining_this_month === 0) {
      tipContext.show(`${uploadPolicy.tier} tier upload limit reached: ${uploadPolicy.max_files_per_month} files per month.`);
      return;
    }

    drawerFileInputRef.current?.click();
  }, [tipContext, uploadPolicy]);

  const handleStoredFilePicked = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    try {
      const [firstFile] = selectedFiles;
      if (firstFile) {
        await uploadFile(firstFile);
        tipContext.showSuccess('File uploaded');
      }
    } catch (error: any) {
      tipContext.show(error?.message || 'Unable to upload that file right now.');
    } finally {
      event.target.value = '';
    }
  }, [tipContext, uploadFile]);

  const handleNewConversation = () => {
    resetConversation();
    setActiveDrawerTab('chat');
    setActiveAssistantTab('ask-indie');
  };

  const buildFullViewHandoff = useCallback((nextConversationId?: string): OrbitChatViewHandoff | undefined => {
    const resolvedConversationId = nextConversationId ?? conversationId;
    const shouldUseLiveConversationState = !nextConversationId || nextConversationId === conversationId;

    if (shouldUseLiveConversationState) {
      return {
        conversationId: resolvedConversationId,
        messages,
        isStreaming: isStreaming || isLoadingHistory,
        streamingMessageId,
        contextChips,
        contextUploads,
      };
    }

    if (!resolvedConversationId) {
      return undefined;
    }

    return {
      conversationId: resolvedConversationId,
    };
  }, [contextChips, contextUploads, conversationId, isLoadingHistory, isStreaming, messages, streamingMessageId]);

  const handleOpenFullView = useCallback((source: 'composer' | 'history' | 'header_action', nextConversationId?: string) => {
    const handoff = buildFullViewHandoff(nextConversationId);

    trackEvent('V3 Ask Indie Event', {
      action: 'Full Screen Handoff',
      clientCode: resolvedClientCode,
      source,
      mode,
      conversationId: handoff?.conversationId || '',
    });
    onOpenFullView?.(handoff);
  }, [buildFullViewHandoff, mode, onOpenFullView, resolvedClientCode]);

  const formatSavedLabel = React.useCallback((content: string, prefix = 'Saved prompt') => {
    const singleLine = content.replace(/\s+/g, ' ').trim();
    if (!singleLine) return prefix;
    return singleLine.length > 60 ? `${singleLine.slice(0, 57)}…` : singleLine;
  }, []);

  const handleSavePrompt = React.useCallback(async (message: ChatMessage) => {
    try {
      await addPrompt({
        label: formatSavedLabel(message.content, message.role === 'assistant' ? 'Assistant prompt' : 'My prompt'),
        promptText: message.content,
      });
      tipContext.showSuccess('Saved to My Prompts');
    } catch {
      tipContext.show('Unable to save this prompt right now.');
    }
  }, [addPrompt, formatSavedLabel, tipContext]);

  const handleSaveArtifact = React.useCallback(async (message: ChatMessage, overrides?: SaveArtifactOverrides) => {
    try {
      await saveArtifact({
        title: formatSavedLabel(message.content, 'Saved analysis'),
        artifact_type: 'summary',
        content_markdown: message.content,
        conversation_id: conversationId,
        source_message_id: Number(String(message.id).replace(/^hist-/, '')) || undefined,
        ...overrides,
      });
      tipContext.showSuccess('Artifact saved');
    } catch (err) {
      tipContext.show('Unable to save this artifact right now.');
      throw err;
    }
  }, [conversationId, formatSavedLabel, saveArtifact, tipContext]);

  const sendAskIndieMessage = React.useCallback((text: string) => {
    if (maintenanceModeActive) {
      tipContext.show('Ask Indie is temporarily unavailable while maintenance is enabled.');
      return;
    }

    sendMessage(text);
  }, [maintenanceModeActive, sendMessage, tipContext]);

  const openConversationHistoryItem = React.useCallback((nextConversationId: string) => {
    if (maintenanceModeActive) {
      tipContext.show('Conversation history is temporarily unavailable while Ask Indie maintenance is enabled.');
      return;
    }

    handleOpenFullView('history', nextConversationId);
  }, [handleOpenFullView, maintenanceModeActive, tipContext]);

  const renderAskIndieSurface = () => (
    <>
      {maintenanceBanner.is_enabled && maintenanceBanner.message ? (
        <OrbitMaintenanceBanner message={maintenanceBanner.message} />
      ) : null}
      <IndieChatBody
        messages={messages}
        onSuggestionClick={sendAskIndieMessage}
        streamingMessageId={streamingMessageId}
        isStreaming={isStreaming || isLoadingHistory}
        context={isDrawerStyledMode ? "Management One's AI advisor" : clientLabel}
        conversationId={conversationId}
        clientId={clientId}
        quotaExceeded={quotaExceeded}
        onFeedback={submitFeedback}
        onSavePrompt={handleSavePrompt}
        onSaveArtifact={handleSaveArtifact}
        artifacts={artifacts}
        surface={isDrawerStyledMode ? 'drawer' : 'default'}
        userName={userName}
        hideStartPrompts={maintenanceModeActive}
        disableSuggestionClicks={maintenanceModeActive}
      />
      <IndieChatInput
        onSend={sendAskIndieMessage}
        onAttachFile={handleAttachFile}
        attachedUploads={contextUploads}
        onRemoveUpload={removeContextUpload}
        placeholder={isDrawerStyledMode ? 'Ask anything about your store…' : 'Ask Indie anything about this client…'}
        disabled={maintenanceModeActive}
        isStreaming={isStreaming || isLoadingHistory}
        surface={isDrawerStyledMode ? 'drawer' : 'default'}
        onOpenFullView={() => handleOpenFullView('composer')}
        onOpenPrompts={() => setActiveDrawerTab('prompts')}
      />
    </>
  );

  const renderIndieInsightSurface = () => (
    <div className="flex-1 min-h-0 overflow-hidden bg-[linear-gradient(180deg,#fcfbff_0%,#f6f1fb_100%)] p-4">
      <IndieChatPanel
        mode="page"
        client={client}
        onClose={undefined}
        onToggleDock={undefined}
        reportAssistant={{
          ...pageAssistant,
          enabled: true,
          clientId: reportAssistantClientId,
        }}
        embedded
      />
    </div>
  );

  const renderActiveChatSurface = () => (
    !hasPageAssistant || activeAssistantTab === 'ask-indie'
      ? renderAskIndieSurface()
      : renderIndieInsightSurface()
  );

  const renderDrawerTabContent = () => {
    if (activeDrawerTab === 'chat') return renderActiveChatSurface();
    if (activeDrawerTab === 'history') return (
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="mb-2.5 text-[9px] font-semibold uppercase tracking-[0.98px] text-[#98A2B3]">Recent conversations</p>
        {conversationsLoading ? (
          <p className="py-6 text-center text-xs text-[#98A2B3]">Loading history…</p>
        ) : conversations.length === 0 ? (
          <p className="py-6 text-center text-xs text-[#98A2B3]">No conversations yet</p>
        ) : (
          conversations.map((conversation) => {
            const isShared = conversation.conversation_visibility === 'shared';
            return (
              <button
                key={conversation.conversation_id}
                type="button"
                disabled={maintenanceModeActive}
                onClick={() => openConversationHistoryItem(conversation.conversation_id)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-geist text-[14px] font-medium leading-[16.5px] text-[#344054]">
                    {conversation.first_user_message || 'Untitled conversation'}
                  </span>
                </span>
                {isShared ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[16px] bg-[#EFF8FF] text-[10px] font-medium leading-4 tracking-[0.1px] text-[#1570EF] font-geist shrink-0">
                    <HistorySharedIcon />
                    <span>Shared</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[16px] bg-[#F9FAFB] text-[10px] font-medium leading-4 tracking-[0.1px] text-[#667085] font-geist shrink-0">
                    <HistoryLockIcon />
                    <span>Private</span>
                  </span>
                )}
              </button>
            );
          })
        )}
        {maintenanceModeActive && conversations.length > 0 ? (
          <p className="py-2 text-xs text-[#98A2B3]">Conversation history cannot be opened while the Ask Indie maintenance banner is active.</p>
        ) : null}
      </div>
    );
    if (activeDrawerTab === 'prompts') return (
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.6px] text-[#98A2B3]">Prompts</p>
        {maintenanceModeActive
          ? <p className="py-2 text-xs text-[#98A2B3]">Prompt shortcuts are unavailable while the Ask Indie maintenance banner is active.</p>
          : [...clientPrompts, ...m1Prompts].map((prompt) => <button key={`${prompt.id}-${prompt.label}`} type="button" onClick={() => { sendAskIndieMessage(prompt.label); setActiveDrawerTab('chat'); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-[#F9FAFB]"><span className="flex h-6 w-6 items-center justify-center rounded-md" style={{ background: prompt.iconBg }}>{prompt.icon}</span><span className="text-[11px] text-[#344054]">{prompt.label}</span></button>)}
      </div>
    );
    if (activeDrawerTab === 'files') return (
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <input ref={drawerFileInputRef} type="file" className="hidden" onChange={handleStoredFilePicked} />
        <button type="button" disabled={filesUploading || uploadPolicy?.files_remaining_this_month === 0} onClick={handleOpenStoredFilePicker} className="mb-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-[#C3BFFA] bg-[#EEEDFE] py-2 text-[11px] font-medium text-[#534AB7] disabled:opacity-50"><Paperclip className="h-3.5 w-3.5" />{filesUploading ? 'Uploading…' : 'Add file'}</button>
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.6px] text-[#98A2B3]">Available files</p>
        {filesLoading ? <p className="py-6 text-center text-xs text-[#98A2B3]">Loading files…</p> : files.length === 0 ? <p className="py-6 text-center text-xs text-[#98A2B3]">No files uploaded yet</p> : files.map((file) => <div key={file.id} className="flex items-center gap-2 border-b border-[#F2F4F7] py-2"><FileText className="h-4 w-4 text-[#667085]" /><span className="min-w-0 flex-1 truncate text-[11px] text-[#344054]">{file.original_filename}</span></div>)}
        <p className="mb-2 mt-4 text-[10px] font-medium uppercase tracking-[0.6px] text-[#98A2B3]">Made by Indie</p>
        <IndieGeneratedFilesTab clientId={clientId} conversationId={conversationId} generatedOnly />
      </div>
    );
    return (
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.6px] text-[#98A2B3]">Saved by Indie</p>
        {artifactsLoading ? <p className="py-6 text-center text-xs text-[#98A2B3]">Loading artifacts…</p> : artifacts.length === 0 ? <p className="py-6 text-center text-xs text-[#98A2B3]">No saved artifacts yet</p> : artifacts.map((artifact) => <button key={artifact.id} type="button" onClick={() => { sendAskIndieMessage(artifact.content_markdown || artifact.preview_text || artifact.title); setActiveDrawerTab('chat'); }} className="mb-2 flex w-full flex-col rounded-[10px] border border-[#EAECF0] p-3 text-left hover:bg-[#F9FAFB]"><span className="text-xs font-medium text-[#101828]">{artifact.title}</span><span className="mt-1 line-clamp-2 text-[11px] text-[#667085]">{artifact.preview_text || artifact.content_markdown}</span></button>)}
      </div>
    );
  };

  // Container sizing by mode
  const containerClass = [
    'indie-chat flex flex-col overflow-hidden bg-white',
    isDrawerStyledMode
      ? 'rounded-xl border border-[#D5D7DA] bg-white shadow-[0_1px_2px_rgba(10,13,18,0.05)]'
      : 'rounded-[16px] shadow-[0_10px_35px_rgba(40,22,60,0.14)] border border-[rgba(116,88,143,0.22)]',
    isStandalonePage ? 'h-full w-full' : 'h-full',
  ].join(' ');

  return (
    <div className={containerClass}>
      {isDrawerStyledMode ? (
        <>
          <div className="flex items-start justify-between gap-4 px-4 pt-4">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[#EEEDFE]">
                <SparkleIcon />
              </div>
              <div className="flex min-w-0 flex-col pt-0.5">
                <h2 className="truncate text-[13px] font-semibold leading-[18px] text-[#101828]">Ask Indie</h2>
                <p className="truncate text-[10px] leading-[15px] text-[#98A2B3]">AI Retail Advisor</p>
              </div>
            </div>
            {!isStandalonePage && (
              <div className="flex shrink-0 items-center gap-0.5">
                {onToggleDock && (
                  <button
                    onClick={isInlineMode ? onToggleDock : () => handleOpenFullView('header_action')}
                    title={isInlineMode ? 'Return to flyout' : 'Open full view'}
                    aria-label={isInlineMode ? 'Return to flyout' : 'Open full view'}
                    className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#F2F4F7] transition-colors"
                  >
                    {isInlineMode
                      ? <Minimize2 className="w-4 h-4 text-[#A4A7AE]" />
                      : <ArrowUpRight className="w-4 h-4 text-[#A4A7AE]" />
                    }
                  </button>
                )}
                <button type="button" aria-label="New conversation" onClick={handleNewConversation} className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#F2F4F7]">
                  <Plus className="h-[13px] w-[13px] text-[#98A2B3]" />
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    title="Close chat"
                    className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#F2F4F7] transition-colors"
                  >
                    <X className="w-4 h-4 text-[#A4A7AE]" />
                  </button>
                )}
              </div>
            )}
          </div>
          <nav className="mt-4 flex items-center overflow-x-auto border-y border-[#EAECF0] px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Ask Indie drawer views">
            {([
              ['chat', 'Chat', Sparkles], ['history', 'History', History], ['prompts', 'Prompts', MessageCircle], ['files', 'Files', FileText], ['artifacts', 'Artifacts', Archive],
            ] as Array<[DrawerTab, string, React.ElementType]>).map(([id, label, Icon]) => <button key={id} type="button" onClick={() => setActiveDrawerTab(id)} className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] transition-colors ${activeDrawerTab === id ? 'border-[#101828] font-semibold text-[#101828]' : 'border-transparent text-[#98A2B3] hover:text-[#667085]'}`}><Icon className="h-3.5 w-3.5" />{label}{id === 'artifacts' && artifacts.length > 0 && <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#6366F1] px-1 text-[9px] text-white">{artifacts.length}</span>}</button>)}
          </nav>
        </>
      ) : (
        <div
          className="flex items-start justify-between px-[18px] py-[14px] border-b border-[rgba(255,255,255,0.08)]"
          style={{ background: 'linear-gradient(135deg, #4d3660 0%, #2d1f3f 100%)' }}
        >
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2.5">
              <div className="w-[34px] h-[34px] flex items-center justify-center rounded-[12px] bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.18)] flex-shrink-0">
                <SparkleIcon />
              </div>
              <div>
                <p className="text-white text-[15px] font-bold leading-[1.2]">Ask Indie</p>
                <p className="text-[rgba(255,255,255,0.75)] text-[11px]">Retail consultant chat surface</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 self-start px-[9px] py-[3px] rounded-full bg-[rgba(255,255,255,0.12)] border border-[rgba(255,255,255,0.18)]">
              <ChevronDown className="w-3 h-3 text-[rgba(255,255,255,0.7)]" />
              <span className="text-white text-[11px] font-medium">{clientLabel}</span>
            </div>
          </div>
          {!isStandalonePage && (
            <div className="flex items-center gap-1 flex-shrink-0 mt-1">
              {onToggleDock && (
                <button
                  onClick={onToggleDock}
                  title={isInlineMode ? 'Return to flyout' : 'Embed on page'}
                  className="w-7 h-7 flex items-center justify-center rounded-[7px] text-white hover:bg-[rgba(255,255,255,0.12)] transition-colors"
                >
                  {isInlineMode
                    ? <Minimize2 className="w-3.5 h-3.5" />
                    : <Maximize2 className="w-3.5 h-3.5" />
                  }
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  title="Close chat"
                  className="w-7 h-7 flex items-center justify-center rounded-[7px] text-white hover:bg-[rgba(255,255,255,0.12)] transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Chat body ── */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        {hasPageAssistant && (
          <div className="px-4 pt-4 pb-2 border-b border-[#E9EAEB] bg-white">
            <div className="inline-flex rounded-xl border border-[#E9EAEB] bg-[#FAFAFA] p-1 gap-1">
              {[
                { id: 'ask-indie', label: 'Ask Indie' },
                { id: 'indie-insight', label: 'Indie Insight' },
              ].map((tab) => {
                const isActive = activeAssistantTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveAssistantTab(tab.id as AssistantTab)}
                    className={`px-3.5 py-2 rounded-[10px] text-sm font-semibold transition-colors ${isActive ? 'bg-[#5E3D79] text-white shadow-sm' : 'text-[#535862] hover:bg-white'}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isDrawerStyledMode ? renderDrawerTabContent() : renderActiveChatSurface()}
      </div>
    </div>
  );
};

/**
 * SPS-5749: keyed on the selected client so React remounts the whole chat when
 * the client changes. Every piece of per-client state (conversation id,
 * transcript, uploads, history list, drawer state, input draft) is dropped in
 * one step, and the unmount cleanup in useOrbitChat aborts a stream still
 * running for the previous client. The key lives here, on the export, so every
 * mount site (layouts and pages) is covered, including future ones.
 */
const V3IndieChatPanelForClient: React.FC<Props> = (props) => (
  <V3IndieChatPanel key={props.clientId ?? 0} {...props} />
);
V3IndieChatPanelForClient.displayName = 'V3IndieChatPanel';

export default V3IndieChatPanelForClient;